import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';
import HormuzHubNav from '../../../components/HormuzHubNav';
import { chokepointsAPI } from '../../../services/api';
import {
  getOrganizationSchema,
  getBreadcrumbSchema,
  getDatasetSchema,
  getEventSchema,
} from '../../../utils/seoSchemas';
import { PieChart, Tooltip, ResponsiveContainer, Pie, Cell } from 'recharts';
import { PieChart as PieIcon, Globe2, Droplet, Map } from 'lucide-react';

const HormuzStatistics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await chokepointsAPI.getStatistics('hormuz');
        setData(res.data);
      } catch (e) {
        console.error('Failed to load statistics', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Key historical events that are referenced in statistics content
  // These ensure proper Event schemas are included for SEO
  const keyHistoricalEvents = [
    {
      title: 'Tanker War (1984-1988)',
      summary: 'Iran-Iraq conflict led to attacks on oil tankers, disrupting shipping and causing significant oil price volatility.',
      created_at: '1984-01-01',
    },
    {
      title: 'Operation Praying Mantis (1988)',
      summary: 'US naval operation in response to Iranian mining of the strait, demonstrating international commitment to free navigation.',
      created_at: '1988-04-18',
    },
    {
      title: '2019 Tanker Attacks',
      summary: 'Multiple tanker attacks attributed to Iran raised concerns about shipping security and oil supply disruptions.',
      created_at: '2019-06-13',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const stats = data?.statistics || {
    dailyOilFlow: 21000000,
    globalOilPercentage: 21,
    seaborneShare: 30,
    narrowestWidthNm: 21,
  };

  const title = 'Strait of Hormuz Statistics | Key Metrics & Data';
  const description = `Strait of Hormuz statistics: ${stats.dailyOilFlow.toLocaleString()} barrels/day, ${stats.globalOilPercentage}% of global oil, ${stats.seaborneShare}% of seaborne trade. Complete data dashboard with interpretation of what these numbers mean for risk and trade.`;

  // Generate Event schemas for key historical events
  const eventSchemas = keyHistoricalEvents.map((event) => getEventSchema(event));

  const jsonLd = [
    getOrganizationSchema(),
    getBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Strait of Hormuz', path: '/strait-of-hormuz' },
      { name: 'Statistics', path: '/strait-of-hormuz/statistics' },
    ]),
    getDatasetSchema(
      'Strait of Hormuz Statistics',
      description,
      {
        path: '/strait-of-hormuz/statistics',
        keywords: ['oil flow', 'statistics', 'metrics', 'data', 'chokepoint'],
        datePublished: data?.article?.created_at || '2026-03-02T00:00:00Z',
        dateModified: data?.article?.updated_at || '2026-03-02T00:00:00Z',
        variableMeasured: [
          `Daily Oil Flow: ${(stats.dailyOilFlow / 1000000).toFixed(1)}M barrels/day`,
          `Global Oil Share: ${stats.globalOilPercentage}%`,
          `Seaborne Trade Share: ${stats.seaborneShare}%`,
          `Narrowest Width: ${stats.narrowestWidthNm} nautical miles`,
        ],
      }
    ),
    ...eventSchemas,
  ];

  const pieData = [
    { name: 'Via Hormuz', value: stats.globalOilPercentage },
    { name: 'Other Routes', value: 100 - stats.globalOilPercentage },
  ];

  const COLORS = ['#3b82f6', '#e5e7eb'];

  return (
    <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEOHead
        title={title}
        description={description}
        path="/strait-of-hormuz/statistics"
        keywords="Hormuz statistics, Strait of Hormuz data, oil flow statistics, Hormuz metrics"
        jsonLd={jsonLd}
        image="/api/og-image/hormuz-statistics.png"
        imageAlt="Strait of Hormuz Statistics - Oil flow data and metrics"
      />

      <HormuzHubNav />

      {/* A. Overview */}
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-2">
          Strait of Hormuz Statistics
        </h1>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-2">
          Last updated: March 2026 · Values are indicative ranges compiled from public sources and
          are intended for high‑level orientation, not precision forecasting.
        </p>
        <p className="text-sm text-slate-600 dark:text-on-surface-variant leading-relaxed">
          This page summarises core metrics that define the scale of the Strait of Hormuz: daily oil
          flows, the share of global consumption routed through the strait, and basic physical
          characteristics. These indicators are the starting point for scenario analysis on the{' '}
          <Link
            to="/strait-of-hormuz/economic-impact"
            className="text-primary-600 dark:text-primary underline"
          >
            Economic Impact
          </Link>{' '}
          page and the assumptions used in the{' '}
          <Link
            to="/strait-of-hormuz/oil-impact-calculator"
            className="text-primary-600 dark:text-primary underline"
          >
            Oil Impact Calculator
          </Link>
          .
        </p>
      </header>

      {/* Key Stats Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          label="Daily Oil Flow"
          value={`${(stats.dailyOilFlow / 1000000).toFixed(1)}M`}
          unit="barrels/day"
          icon={Droplet}
        />
        <StatCard
          label="Global Oil Share"
          value={`${stats.globalOilPercentage}%`}
          unit="of total consumption"
          icon={PieIcon}
        />
        <StatCard
          label="Seaborne Trade"
          value={`${stats.seaborneShare}%`}
          unit="of seaborne oil"
          icon={Globe2}
        />
        <StatCard
          label="Narrowest Width"
          value={`${stats.narrowestWidthNm}`}
          unit="nautical miles"
          icon={Map}
        />
      </div>

      {/* Charts with Interpretation */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-4">
            Global Oil Flow Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <p className="mt-4 text-xs text-slate-700 dark:text-on-surface-variant">
            A share of around one‑fifth of global oil consumption moving through a single maritime
            corridor helps explain why even limited disruptions can have outsized effects on market
            sentiment and freight pricing.
          </p>
        </div>

        <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-4">
            Daily Volume Breakdown
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600 dark:text-on-surface-variant">Crude Oil</span>
                <span className="font-medium text-slate-900 dark:text-on-surface">~17M bbl/day</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-surface-container-low rounded-full h-2">
                <div className="bg-primary-600 h-2 rounded-full" style={{ width: '81%' }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600 dark:text-on-surface-variant">Refined Products</span>
                <span className="font-medium text-slate-900 dark:text-on-surface">~4M bbl/day</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-surface-container-low rounded-full h-2">
                <div className="bg-primary-400 h-2 rounded-full" style={{ width: '19%' }} />
              </div>
            </div>
          </div>
          <p className="mt-4 text-xs text-slate-700 dark:text-on-surface-variant">
            A large crude stream supports refineries in multiple regions, while product flows supply
            regional fuel markets. Disruptions therefore affect both upstream producers and
            downstream consumers.
          </p>
        </div>
      </div>

      {/* Interpretive Notes */}
      <section className="mb-8 rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-on-surface mb-3">
          How to Read These Numbers
        </h2>
        <ul className="space-y-2 text-sm text-slate-700 dark:text-on-surface-variant list-disc list-inside">
          <li>
            Volumes vary month to month. The figures here are rounded and intended to show that
            Hormuz carries the equivalent of many large fields&apos; worth of production every day.
          </li>
          <li>
            The global share metrics matter because they inform how sensitive benchmark prices can
            be to news about the strait. A higher share typically means a larger potential impact
            from a given disruption.
          </li>
          <li>
            Narrow physical dimensions and heavy traffic density make operational safety, routing
            decisions, and naval presence central to risk assessment.
          </li>
        </ul>
      </section>

      {/* Internal Links */}
      <section className="mb-8 bg-slate-100 dark:bg-surface-container-low rounded-lg border border-slate-200 dark:border-outline-variant/15 p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-3">
          Connect the Metrics to Scenarios and Country Exposure
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant mb-2">
          These statistics provide the backbone for the more narrative pages in the Hormuz hub:
        </p>
        <ul className="space-y-2 text-sm text-primary-700 dark:text-primary-300 list-disc list-inside">
          <li>
            <Link to="/strait-of-hormuz/economic-impact" className="underline">
              Economic Impact
            </Link>{' '}
            – uses these volumes to frame scenario‑based price and macro ranges.
          </li>
          <li>
            <Link to="/strait-of-hormuz/countries-dependent" className="underline">
              Countries Dependent
            </Link>{' '}
            – translates flows into relative exposure across major importers.
          </li>
          <li>
            <Link to="/strait-of-hormuz/oil-impact-calculator" className="underline">
              Oil Impact Calculator
            </Link>{' '}
            – lets you stress‑test how different disruption assumptions interact with base prices.
          </li>
        </ul>
      </section>

      {/* Additional Content from CMS (if any) */}
      {data?.article?.content && (
        <div
          className="prose dark:prose-invert max-w-none rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6"
          dangerouslySetInnerHTML={{ __html: data.article.content }}
        />
      )}
    </article>
  );
};

const StatCard = ({ label, value, unit, icon: Icon }) => (
  <div className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm text-slate-600 dark:text-on-surface-variant">{label}</span>
      {Icon && <Icon className="w-5 h-5 text-primary-600" />}
    </div>
    <p className="text-3xl font-bold text-slate-900 dark:text-on-surface mb-1">{value}</p>
    <p className="text-xs text-slate-500 dark:text-on-surface-variant">{unit}</p>
  </div>
);

export default HormuzStatistics;

